import React, { useState } from 'react';
import axios from 'axios';
import './SemanticSearch.css';

const SemanticSearch = () => {
  // Search mode: 'semantic' or 'keyword'
  const [searchMode, setSearchMode] = useState('semantic');
  
  // Semantic search fields
  const [query, setQuery] = useState('');
  const [loanGuid, setLoanGuid] = useState('');
  
  // Keyword search fields
  const [keyword, setKeyword] = useState('');
  
  // Common filters
  const [docCategory, setDocCategory] = useState('');
  const [finalDecision, setFinalDecision] = useState('');
  const [docType, setDocType] = useState('');
  const [company, setCompany] = useState('');
  const [limit, setLimit] = useState(10);
  
  
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const resultsPerPage = 5;

  // Get API endpoints from environment or config
  const SEMANTIC_API = process.env.REACT_APP_VECTOR_SEARCH_API || 
                       window._env_?.REACT_APP_VECTOR_SEARCH_API ||
                       'https://y6quxirmk0.execute-api.us-east-2.amazonaws.com/dev/search';
  
  const KEYWORD_API = process.env.REACT_APP_KEYWORD_SEARCH_API || 
                      window._env_?.REACT_APP_KEYWORD_SEARCH_API ||
                      'https://vlwvc0r3s3.execute-api.us-east-2.amazonaws.com/dev/keyword-search';

  const handleSemanticSearch = async (e, isRetry = false) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    if (!query) {
      setError('Query is required for semantic search');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);
    setCurrentPage(1);

    try {
      const params = new URLSearchParams({
        query: query,
        limit: limit
      });

      if (loanGuid) params.append('loan_guid', loanGuid);
      if (finalDecision) params.append('final_decision', finalDecision);
      if (docType) params.append('doc_type', docType);
      if (company) params.append('company', company);

      console.log('Semantic search params:', params.toString());

      const response = await axios.get(`${SEMANTIC_API}?${params.toString()}`, {
        timeout: 35000  // 35 second timeout (slightly longer than API Gateway 29s)
      });
      setResults({ ...response.data, mode: 'semantic' });
      setLoading(false);
      
    } catch (err) {
      console.error('Semantic search error:', err);
      
      // Retry once on timeout/network error (likely cold start)
      const isTimeoutError = err.code === 'ECONNABORTED' || 
                            err.message?.includes('timeout') ||
                            err.message?.includes('Network Error');
      
      if (!isRetry && isTimeoutError) {
        console.log('First request failed (likely cold start), retrying in 2 seconds...');
        setError('Lambda cold start detected, retrying...');
        setTimeout(() => {
          handleSemanticSearch(null, true);
        }, 2000);
        return;
      }
      
      setError(err.response?.data?.error || err.message || 'Semantic search failed');
      setLoading(false);
    }
  };

  const handleKeywordSearch = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    if (!keyword) {
      setError('Keyword is required');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);
    setCurrentPage(1);

    try {
      const params = new URLSearchParams({
        keyword: keyword,
        limit: limit
      });

      if (loanGuid) params.append('loan_guid', loanGuid);
      if (docType) params.append('doc_type', docType);
      if (company) params.append('company', company);
      if (finalDecision) params.append('final_decision', finalDecision);

      console.log('Keyword search params:', params.toString());

      const response = await axios.get(`${KEYWORD_API}?${params.toString()}`);
      setResults({ ...response.data, mode: 'keyword' });
      
    } catch (err) {
      console.error('Keyword search error:', err);
      setError(err.response?.data?.error || err.message || 'Keyword search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    console.log('handleSearch called, mode:', searchMode);
    if (searchMode === 'semantic') {
      handleSemanticSearch(e);
    } else {
      handleKeywordSearch(e);
    }
  };

  const handleClear = () => {
    setQuery('');
    setKeyword('');
    setLoanGuid('');
    setDocCategory('');
    setFinalDecision('');
    setDocType('');
    setCompany('');
    setResults(null);
    setError(null);
    setSelectedResult(null);
  };

  const openResultModal = (result) => {
    setSelectedResult(result);
  };

  const closeResultModal = () => {
    setSelectedResult(null);
  };

  const getScoreColor = (score) => {
    if (score >= 0.8) return '#4caf50';  // Green
    if (score >= 0.6) return '#ff9800';  // Orange
    return '#f44336';  // Red
  };

  return (
    <div className="semantic-search-container">
      <div className="search-header">
        <h1>Custom Search</h1>
        <p>Search across all auto-approval documents using semantic or keyword search</p>
      </div>

      {/* Search Mode Tabs */}
      <div className="search-mode-tabs">
        <button
          className={`mode-tab ${searchMode === 'semantic' ? 'active' : ''}`}
          onClick={() => setSearchMode('semantic')}
        >
          <i className="fas fa-brain"></i>
          Semantic Search
        </button>
        <button
          className={`mode-tab ${searchMode === 'keyword' ? 'active' : ''}`}
          onClick={() => setSearchMode('keyword')}
        >
          <i className="fas fa-search"></i>
          Keyword Search
        </button>
      </div>

      <div className="search-form-container">
        {/* Left Column: Search Form */}
        <form onSubmit={handleSearch} className="search-form">
          {/* Mode-specific fields */}
          {searchMode === 'semantic' ? (
            <>
              {/* Hero Search Box */}
              <div className="hero-search-section">
                <div className="hero-search-group">
                  <label htmlFor="query" className="hero-label">
                    <i className="fas fa-brain"></i>
                    Ask a Question
                  </label>
                  <input
                    id="query"
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="e.g., What insurance documents are approved? Show documents with premium mismatches..."
                    className="hero-search-input"
                    disabled={loading}
                  />
                </div>
                <p className="hero-hint">💡 Use natural language to search across all documents</p>
              </div>

              {/* Optional Filters - Compact */}
              <div className="filters-section">
                <div className="filters-header">
                  <i className="fas fa-sliders-h"></i>
                  Optional Filters
                </div>
                <div className="filters-grid">
                  <div className="filter-item">
                    <label htmlFor="loanGuid">Loan GUID</label>
                    <input
                      id="loanGuid"
                      type="text"
                      value={loanGuid}
                      onChange={(e) => setLoanGuid(e.target.value)}
                      placeholder="Filter by loan"
                      className="filter-input"
                      disabled={loading}
                    />
                  </div>
                  <div className="filter-item">
                    <label htmlFor="docType">Doc Type</label>
                    <select
                      id="docType"
                      value={docType}
                      onChange={(e) => setDocType(e.target.value)}
                      className="filter-input"
                      disabled={loading}
                    >
                      <option value="">All Types</option>
                      <option value="HOI_ProofOfInsurance">Insurance</option>
                      <option value="TITLE_REPORT">Title</option>
                    </select>
                  </div>
                  <div className="filter-item">
                    <label htmlFor="company">Company</label>
                    <select
                      id="company"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      className="filter-input"
                      disabled={loading}
                    >
                      <option value="">All</option>
                      <option value="gri">GRI</option>
                      <option value="nfm">NFM</option>
                      <option value="cms">CMS</option>
                      <option value="gra">GRA</option>
                      <option value="op">OP</option>
                      <option value="kbhs">KBHS</option>
                    </select>
                  </div>
                  <div className="filter-item">
                    <label htmlFor="finalDecision">Decision</label>
                    <select
                      id="finalDecision"
                      value={finalDecision}
                      onChange={(e) => setFinalDecision(e.target.value)}
                      className="filter-input"
                      disabled={loading}
                    >
                      <option value="">All</option>
                      <option value="approved">Approved</option>
                      <option value="not_approved">Not Approved</option>
                      <option value="manual_review">Manual Review</option>
                    </select>
                  </div>
                  <div className="filter-item">
                    <label htmlFor="limit">Limit</label>
                    <input
                      id="limit"
                      type="number"
                      value={limit}
                      onChange={(e) => setLimit(parseInt(e.target.value) || 10)}
                      min="1"
                      max="50"
                      className="filter-input"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Hero Search Box for Keyword */}
              <div className="hero-search-section">
                <div className="hero-search-group">
                  <label htmlFor="keyword" className="hero-label">
                    <i className="fas fa-search"></i>
                    Search Keyword
                  </label>
                  <input
                    id="keyword"
                    type="text"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="e.g., Meghan, Policy123, Acord..."
                    className="hero-search-input"
                    disabled={loading}
                  />
                </div>
                <p className="hero-hint">🔍 Search for exact keywords in documents</p>
              </div>

              {/* Optional Filters - Compact */}
              <div className="filters-section">
                <div className="filters-header">
                  <i className="fas fa-sliders-h"></i>
                  Optional Filters
                </div>
                <div className="filters-grid">
                  <div className="filter-item">
                    <label htmlFor="loanGuid">Loan GUID</label>
                    <input
                      id="loanGuid"
                      type="text"
                      value={loanGuid}
                      onChange={(e) => setLoanGuid(e.target.value)}
                      placeholder="Filter by loan"
                      className="filter-input"
                      disabled={loading}
                    />
                  </div>
                  <div className="filter-item">
                    <label htmlFor="docType">Doc Type</label>
                    <select
                      id="docType"
                      value={docType}
                      onChange={(e) => setDocType(e.target.value)}
                      className="filter-input"
                      disabled={loading}
                    >
                      <option value="">All Types</option>
                      <option value="HOI_ProofOfInsurance">Insurance</option>
                      <option value="TITLE_REPORT">Title</option>
                    </select>
                  </div>
                  <div className="filter-item">
                    <label htmlFor="company">Company</label>
                    <select
                      id="company"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      className="filter-input"
                      disabled={loading}
                    >
                      <option value="">All</option>
                      <option value="gri">GRI</option>
                      <option value="nfm">NFM</option>
                      <option value="cms">CMS</option>
                      <option value="gra">GRA</option>
                      <option value="op">OP</option>
                      <option value="kbhs">KBHS</option>
                    </select>
                  </div>
                  <div className="filter-item">
                    <label htmlFor="finalDecision">Decision</label>
                    <select
                      id="finalDecision"
                      value={finalDecision}
                      onChange={(e) => setFinalDecision(e.target.value)}
                      className="filter-input"
                      disabled={loading}
                    >
                      <option value="">All</option>
                      <option value="approved">Approved</option>
                      <option value="not_approved">Not Approved</option>
                      <option value="manual_review">Manual Review</option>
                    </select>
                  </div>
                  <div className="filter-item">
                    <label htmlFor="limit">Limit</label>
                    <input
                      id="limit"
                      type="number"
                      value={limit}
                      onChange={(e) => setLimit(parseInt(e.target.value) || 50)}
                      min="1"
                      max="100"
                      className="filter-input"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="form-actions">
            <button type="submit" className="btn-search" disabled={loading}>
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Searching...
                </>
              ) : (
                <>
                  <i className="fas fa-search"></i>
                  Search
                </>
              )}
            </button>
            <button type="button" className="btn-clear" onClick={handleClear} disabled={loading}>
              <i className="fas fa-times"></i>
              Clear
            </button>
          </div>
        </form>

        {/* Right Column: Results */}
        <div className="results-column">
          {error && (
            <div className="error-container">
              <p>{error}</p>
            </div>
          )}

          {results && (
            <div className="results-container">
          <div className="results-header">
            <h2>Search Results</h2>
            <div className="results-meta">
              <span className="results-count">{results.count} results found</span>
              <span className="results-query">
                {results.mode === 'semantic' ? `Query: "${results.query}"` : `Keyword: "${results.keyword}"`}
              </span>
            </div>
          </div>

          {results.count === 0 ? (
            <div className="no-results">
              <p>No results found. Try adjusting your {results.mode === 'semantic' ? 'query' : 'keyword'} or filters.</p>
            </div>
          ) : (
            <>
              <div className="results-list">
                {results.results
                  .slice((currentPage - 1) * resultsPerPage, currentPage * resultsPerPage)
                  .map((result, index) => {
                    const actualIndex = (currentPage - 1) * resultsPerPage + index;
                    return (
                      <div key={actualIndex} className="result-card" onClick={() => openResultModal(result)}>
                        <div className="result-header">
                          {results.mode === 'semantic' && result.score && (
                            <div className="result-score">
                              <div 
                                className="score-badge"
                                style={{ backgroundColor: getScoreColor(result.score) }}
                              >
                                {(result.score * 100).toFixed(0)}%
                              </div>
                            </div>
                          )}
                          <div className="result-summary">
                            <div className="result-title">
                              {results.mode === 'semantic' ? (
                                <>
                                  {result.chunk_kind === 'rule' ? (
                                    <span className="rule-badge">Rule</span>
                                  ) : (
                                    <span className="fact-badge">Fact</span>
                                  )}
                                  <span className="result-key">{result.chunk_key?.replace(/_/g, ' ')}</span>
                                </>
                              ) : (
                                <>
                                  <span className="doc-badge">Keyword Match</span>
                                  <span className="result-snippet">
                                    {result.snippet || result.chunk_text || 'No preview available'}
                                  </span>
                                </>
                              )}
                            </div>
                            <div className="result-meta-line">
                              <span className="meta-item">{result.doc_type}</span>
                              <span className="meta-separator">•</span>
                              <span className="meta-item">{result.company?.toUpperCase()}</span>
                              {result.page > 0 && (
                                <>
                                  <span className="meta-separator">•</span>
                                  <span className="meta-item">Page {result.page}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="expand-icon">
                            <i className="fas fa-external-link-alt"></i>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
              
              {/* Pagination */}
              {results.count > resultsPerPage && (
                <div className="pagination">
                  <button 
                    className="pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    <i className="fas fa-chevron-left"></i>
                    Previous
                  </button>
                  
                  <div className="pagination-info">
                    <span className="pagination-text">
                      Page {currentPage} of {Math.ceil(results.count / resultsPerPage)}
                    </span>
                    <span className="pagination-range">
                      Showing {((currentPage - 1) * resultsPerPage) + 1}-{Math.min(currentPage * resultsPerPage, results.count)} of {results.count}
                    </span>
                  </div>
                  
                  <button 
                    className="pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.min(Math.ceil(results.count / resultsPerPage), prev + 1))}
                    disabled={currentPage >= Math.ceil(results.count / resultsPerPage)}
                  >
                    Next
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!results && !error && (
        <div className="results-placeholder">
          <i className="fas fa-search"></i>
          <h3>Ready to search</h3>
          <p>Enter your query and click Search to see results here</p>
        </div>
      )}
        </div>
      </div>

      {/* Modal for result details */}
      {selectedResult && (
        <div className="modal-overlay" onClick={closeResultModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-file-alt"></i>
                Result Details
              </h2>
              <button className="modal-close" onClick={closeResultModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="modal-body">
              {/* Score Badge (if semantic) */}
              {results?.mode === 'semantic' && selectedResult.score && (
                <div className="modal-score">
                  <span className="modal-score-label">Relevance Score:</span>
                  <div 
                    className="score-badge"
                    style={{ backgroundColor: getScoreColor(selectedResult.score) }}
                  >
                    {(selectedResult.score * 100).toFixed(0)}%
                  </div>
                </div>
              )}

              {/* Content Section */}
              <div className="modal-section">
                <h3>
                  <i className="fas fa-align-left"></i>
                  Content
                </h3>
                <div className="modal-content-text">
                  {results?.mode === 'semantic' 
                    ? (selectedResult.chunk_text || 'No text available')
                    : (selectedResult.snippet || 'Keyword found in document')}
                </div>
              </div>

              {/* Document Details Section */}
              <div className="modal-section">
                <h3>
                  <i className="fas fa-info-circle"></i>
                  Document Details
                </h3>
                <div className="modal-details-grid">
                  {selectedResult.doc_id && (
                    <div className="modal-detail-item">
                      <span className="modal-detail-label">Document ID</span>
                      <span className="modal-detail-value">{selectedResult.doc_id}</span>
                    </div>
                  )}
                  {selectedResult.loan_guid && (
                    <div className="modal-detail-item">
                      <span className="modal-detail-label">Loan GUID</span>
                      <span className="modal-detail-value">{selectedResult.loan_guid}</span>
                    </div>
                  )}
                  {selectedResult.file_name && (
                    <div className="modal-detail-item">
                      <span className="modal-detail-label">File Name</span>
                      <span className="modal-detail-value modal-file-name">{selectedResult.file_name}</span>
                    </div>
                  )}
                  {selectedResult.doc_type && (
                    <div className="modal-detail-item">
                      <span className="modal-detail-label">Document Type</span>
                      <span className="modal-detail-value">{selectedResult.doc_type}</span>
                    </div>
                  )}
                  {selectedResult.company && (
                    <div className="modal-detail-item">
                      <span className="modal-detail-label">Company</span>
                      <span className="modal-detail-value">{selectedResult.company.toUpperCase()}</span>
                    </div>
                  )}
                  {selectedResult.page !== undefined && selectedResult.page > 0 && (
                    <div className="modal-detail-item">
                      <span className="modal-detail-label">Page</span>
                      <span className="modal-detail-value">{selectedResult.page}</span>
                    </div>
                  )}
                  {selectedResult.final_decision && (
                    <div className="modal-detail-item">
                      <span className="modal-detail-label">Decision</span>
                      <span className={`modal-detail-value decision-${selectedResult.final_decision}`}>
                        {selectedResult.final_decision.replace(/_/g, ' ').toUpperCase()}
                      </span>
                    </div>
                  )}
                  {selectedResult.chunk_kind && (
                    <div className="modal-detail-item">
                      <span className="modal-detail-label">Chunk Type</span>
                      <span className="modal-detail-value">{selectedResult.chunk_kind.toUpperCase()}</span>
                    </div>
                  )}
                  {selectedResult.chunk_key && (
                    <div className="modal-detail-item">
                      <span className="modal-detail-label">Chunk Key</span>
                      <span className="modal-detail-value">{selectedResult.chunk_key}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SemanticSearch;
